<template>
    <v-container fluid>
        <v-card class="pa-4">
            <v-card-title class="headline mb-4 d-flex justify-space-between align-center">
                <span>Картотека резервов</span>
                <v-btn color="primary" @click="openCreateDialog">
                    <v-icon left>mdi-plus</v-icon>
                    Добавить резерв
                </v-btn>
            </v-card-title>

            <v-card-text>
                <v-data-table
                :headers="headers"
                :items="reserves"
                :loading="loading"
                :search="search"
                class="elevation-1"
                >
                    <template v-slot:top>
                        <v-text-field
                        v-model="search"
                        label="Поиск"
                        class="mx-4"
                        prepend-inner-icon="mdi-magnify"
                        dense
                        clearable
                        />
                    </template>

                    <template v-slot:item.counterparty="{ item }">
                        {{ item.cargoType?.name || 'Не указан' }}
                    </template>

                    <template v-slot:item.reserve="{ item }">
                        {{ item.quantity }}
                    </template>

                    <template v-slot:item.dateStart="{ item }">
                        {{ formatDate(item.dateStart) }}
                    </template>

                    <template v-slot:item.dateEnd="{ item }">
                        {{ formatDate(item.dateEnd) }}
                    </template>

                    <template v-slot:item.actions="{ item }">
                        <v-btn small color="primary" @click="openEditDialog(item)" class="mr-2">
                            <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn small color="error" @click="deleteReserve(item)">
                            <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                    </template>

                    <template v-slot:no-data>
                        <div class="text-center py-4">Нет данных</div>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <!-- Диалог добавления/редактирования -->
        <v-dialog v-model="dialog" max-width="600px">
            <v-card>
                <v-card-title class="dialogTitleBackground white--text">
                    <span>{{ formTitle }}</span>
                    <v-spacer />
                    <v-btn small icon dark @click="closeDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pt-4">
                    <v-form v-model="formValid" ref="form">
                        <v-row>
                            <v-col cols="12">
                                <v-select
                                v-if="editedIndex === -1"
                                v-model="editedItem.cargoTypeId"
                                :items="cargoTypes"
                                item-text="name"
                                item-value="id"
                                label="Контрагент *"
                                :rules="[requiredRule]"
                                dense
                                />
                                <v-text-field
                                v-else
                                :value="getCargoTypeName(editedItem.cargoTypeId)"
                                label="Контрагент"
                                disabled
                                dense
                                />
                            </v-col>

                            <v-col cols="12">
                                <v-text-field
                                v-model="editedItem.quantity"
                                label="Количество *"
                                type="number"
                                step="1"
                                min="0"
                                :rules="[requiredRule, quantityRule]"
                                dense
                                />
                            </v-col>

                            <v-col cols="12" sm="6">
                                <v-menu
                                v-model="dateStartMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                        v-model="dateStartFormatted"
                                        label="Дата начала *"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        :rules="[requiredRule, dateStartRule]"
                                        dense
                                        clearable
                                        @click:clear="editedItem.dateStart = null"
                                        />
                                    </template>
                                    <v-date-picker
                                    v-model="editedItem.dateStart"
                                    no-title
                                    scrollable
                                    locale="ru-ru"
                                    @input="dateStartMenu = false"
                                    :max="editedItem.dateEnd"
                                    />
                                </v-menu>
                            </v-col>

                            <v-col cols="12" sm="6">
                                <v-menu
                                v-model="dateEndMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                        v-model="dateEndFormatted"
                                        label="Дата окончания *"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        :rules="[requiredRule, dateEndRule]"
                                        dense
                                        clearable
                                        @click:clear="editedItem.dateEnd = null"
                                        />
                                    </template>
                                    <v-date-picker
                                    v-model="editedItem.dateEnd"
                                    no-title
                                    scrollable
                                    locale="ru-ru"
                                    @input="dateEndMenu = false"
                                    :min="editedItem.dateStart"
                                    />
                                </v-menu>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn color="grey" @click="closeDialog">Отмена</v-btn>
                    <v-btn
                    color="primary"
                    :loading="saveLoading"
                    @click="saveReserve"
                    :disabled="!formValid"
                    >
                        Сохранить
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Диалог подтверждения удаления -->
        <v-dialog v-model="deleteDialog" max-width="400px">
            <v-card>
                <v-card-title class="dialogTitleBackground white--text">
                    <span>Подтверждение удаления</span>
                    <v-spacer />
                    <v-btn small icon dark @click="deleteDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pt-4">
                    <p>Вы уверены, что хотите удалить резерв?</p>
                    <p class="font-weight-medium">ID: {{ deleteItem?.id }}</p>
                    <p v-if="deleteItem">
                        Контрагент: {{ deleteItem.cargoType?.name || 'Не указан' }}
                    </p>
                    <p v-if="deleteItem">
                        Количество: {{ deleteItem.quantity }}
                    </p>
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn color="grey" @click="deleteDialog = false">Отмена</v-btn>
                    <v-btn color="error" :loading="deleteLoading" @click="confirmDelete">
                        Удалить
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
export default {
    name: 'ReserveRecords',

    data() {
        return {
            reserves: [],
            cargoTypes: [],
            loading: false,
            search: '',
            dialog: false,
            deleteDialog: false,
            editedIndex: -1,
            deleteItem: null,
            formValid: false,
            saveLoading: false,
            deleteLoading: false,
            dateStartMenu: false,
            dateEndMenu: false,
            editedItem: {
                id: null,
                cargoTypeId: null,
                quantity: null,
                dateStart: null,
                dateEnd: null
            },
            defaultItem: {
                id: null,
                cargoTypeId: null,
                quantity: null,
                dateStart: null,
                dateEnd: null
            },
            headers: [
                { text: 'Контрагент', value: 'counterparty', sortable: true },
                { text: 'Резерв', value: 'reserve', sortable: true },
                { text: 'Дата начала', value: 'dateStart', sortable: true },
                { text: 'Дата окончания', value: 'dateEnd', sortable: true },
                { text: 'Действия', value: 'actions', sortable: false, width: '150px' }
            ]
        };
    },

    computed: {
        formTitle() {
            return this.editedIndex === -1 ? 'Добавить резерв' : 'Редактировать резерв';
        },
        dateStartFormatted() {
            if (!this.editedItem.dateStart) return '';
            return this.formatDateForDisplay(this.editedItem.dateStart);
        },
        dateEndFormatted() {
            if (!this.editedItem.dateEnd) return '';
            return this.formatDateForDisplay(this.editedItem.dateEnd);
        }
    },

    watch: {
        'editedItem.dateStart'() {
            if (this.editedItem.dateStart && this.editedItem.dateEnd) {
                this.$nextTick(() => {
                    if (this.$refs.form) {
                        this.$refs.form.validate();
                    }
                });
            }
        },
        'editedItem.dateEnd'() {
            if (this.editedItem.dateEnd && this.editedItem.dateStart) {
                this.$nextTick(() => {
                    if (this.$refs.form) {
                        this.$refs.form.validate();
                    }
                });
            }
        },
        'editedItem.cargoTypeId'() {
            if (this.editedItem.dateStart && this.editedItem.dateEnd) {
                this.$nextTick(() => {
                    if (this.$refs.form) {
                        this.$refs.form.validate();
                    }
                });
            }
        }
    },

    async created() {
        await Promise.all([
            this.loadReserves(),
            this.loadCargoTypes()
        ]);
    },

    methods: {
        // ========== РАБОТА С ДАТАМИ ==========

        normalizeDate(date) {
            if (!date) return null;

            if (date instanceof Date) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            if (typeof date === 'string') {
                const trimmedDate = date.trim();
                if (trimmedDate.includes('T')) return trimmedDate.split('T')[0];
                if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedDate)) return trimmedDate;
                if (/^\d{2}\.\d{2}\.\d{4}$/.test(trimmedDate)) {
                    const [day, month, year] = trimmedDate.split('.');
                    return `${year}-${month}-${day}`;
                }
            }

            return null;
        },

        formatDateForDisplay(date) {
            if (!date) return '';
            const normalized = this.normalizeDate(date);
            if (!normalized) return '';
            const [year, month, day] = normalized.split('-');
            return `${day}.${month}.${year}`;
        },

        formatDate(date) {
            return this.formatDateForDisplay(date);
        },

        hasOverlap(start1, end1, start2, end2) {
            const s1 = this.normalizeDate(start1);
            const e1 = this.normalizeDate(end1);
            const s2 = this.normalizeDate(start2);
            const e2 = this.normalizeDate(end2);

            if (!s1 || !e1 || !s2 || !e2) return false;

            return s1 <= e2 && e1 >= s2;
        },

        // ========== ВАЛИДАЦИЯ ==========

        requiredRule(value) {
            return !!value || 'Это поле обязательно';
        },

        quantityRule(value) {
            if (value === null || value === undefined || value === '') {
                return 'Количество обязательно';
            }
            const num = Number(value);
            if (!Number.isInteger(num)) return 'Количество должно быть целым числом';
            if (num < 0) return 'Количество не может быть отрицательным';
            return true;
        },

        dateStartRule(value) {
            if (!value) return 'Дата начала обязательна';

            const start = this.normalizeDate(value);
            const end = this.normalizeDate(this.editedItem.dateEnd);

            if (end && start > end) {
                return 'Дата начала не может быть позже даты окончания';
            }

            if (this.editedItem.cargoTypeId) {
                const hasOverlap = this.reserves.some((r) => {
                    if (r.isDeleted) return false;
                    if (this.editedIndex !== -1 && r.id === this.editedItem.id) {
                        return false;
                    }

                    return r.cargoTypeId === this.editedItem.cargoTypeId &&
                           this.hasOverlap(
                               start,
                               end || start,
                               r.dateStart,
                               r.dateEnd
                           );
                });

                if (hasOverlap) {
                    return 'Этот период пересекается с существующим резервом';
                }
            }

            return true;
        },

        dateEndRule(value) {
            if (!value) return 'Дата окончания обязательна';

            const start = this.normalizeDate(this.editedItem.dateStart);
            const end = this.normalizeDate(value);

            if (!start) {
                return 'Сначала выберите дату начала';
            }

            if (end < start) {
                return 'Дата окончания не может быть раньше даты начала';
            }

            if (this.editedItem.cargoTypeId) {
                const hasOverlap = this.reserves.some((r) => {
                    if (r.isDeleted) return false;
                    if (this.editedIndex !== -1 && r.id === this.editedItem.id) {
                        return false;
                    }

                    return r.cargoTypeId === this.editedItem.cargoTypeId &&
                           this.hasOverlap(
                               start,
                               end,
                               r.dateStart,
                               r.dateEnd
                           );
                });

                if (hasOverlap) {
                    return 'Этот период пересекается с существующим резервом';
                }
            }

            return true;
        },

        // ========== ЗАГРУЗКА ДАННЫХ ==========

        getCargoTypeName(id) {
            const cargoType = this.cargoTypes.find((c) => c.id === id);
            return cargoType ? cargoType.name : 'Не указан';
        },

        async loadReserves() {
            this.loading = true;
            try {
                const response = await this.axios.get('/reserves', {
                    params: {
                        isDeleted: false
                    }
                });
                this.reserves = Array.isArray(response.data) ? response.data : [];
            } catch (error) {
                console.error('Ошибка загрузки резервов:', error);
                this.$notify('error', 'Ошибка загрузки данных');
                this.reserves = [];
            } finally {
                this.loading = false;
            }
        },

        async loadCargoTypes() {
            try {
                const response = await this.axios.get('/CargoTypes');
                this.cargoTypes = Array.isArray(response.data) ? response.data : [];
            } catch (error) {
                console.error('Ошибка загрузки типов грузов:', error);
                this.$notify('error', 'Ошибка загрузки типов грузов');
                this.cargoTypes = [];
            }
        },

        // ========== ДИАЛОГИ ==========

        openCreateDialog() {
            this.editedIndex = -1;
            this.editedItem = {
                ...this.defaultItem,
                dateStart: null,
                dateEnd: null
            };
            this.dialog = true;
        },

        openEditDialog(item) {
            this.editedIndex = this.reserves.indexOf(item);
            this.editedItem = {
                id: item.id,
                cargoTypeId: item.cargoTypeId,
                quantity: item.quantity,
                dateStart: this.normalizeDate(item.dateStart),
                dateEnd: this.normalizeDate(item.dateEnd),
                originalDateStart: this.normalizeDate(item.dateStart),
                originalDateEnd: this.normalizeDate(item.dateEnd)
            };
            this.dialog = true;
        },

        closeDialog() {
            this.dialog = false;
            this.editedItem = { ...this.defaultItem };
            this.editedIndex = -1;
            this.dateStartMenu = false;
            this.dateEndMenu = false;
        },

        // ========== СОХРАНЕНИЕ ==========

        async saveReserve() {
            if (!this.$refs.form.validate()) {
                return;
            }

            const start = this.normalizeDate(this.editedItem.dateStart);
            const end = this.normalizeDate(this.editedItem.dateEnd);

            if (!start || !end) {
                this.$notify('error', 'Пожалуйста, выберите корректные даты');
                return;
            }

            if (end < start) {
                this.$notify('error', 'Дата окончания не может быть раньше даты начала');
                return;
            }

            if (this.editedItem.cargoTypeId) {
                const hasOverlap = this.reserves.some((r) => {
                    if (r.isDeleted) return false;
                    if (this.editedIndex !== -1 && r.id === this.editedItem.id) {
                        return false;
                    }

                    return r.cargoTypeId === this.editedItem.cargoTypeId &&
                           this.hasOverlap(start, end, r.dateStart, r.dateEnd);
                });

                if (hasOverlap) {
                    this.$notify('error', 'Этот период пересекается с существующим резервом');
                    return;
                }
            }

            this.saveLoading = true;
            try {
                const payload = {
                    quantity: Number(this.editedItem.quantity),
                    dateStart: start,
                    dateEnd: end,
                    isDeleted: false
                };

                if (this.editedIndex === -1) {
                    payload.cargoTypeId = this.editedItem.cargoTypeId;
                }

                let response;
                if (this.editedIndex === -1) {
                    response = await this.axios.post('/reserves', payload);
                    const newItem = {
                        ...response.data,
                        cargoType: this.cargoTypes.find((c) => c.id === response.data.cargoTypeId)
                    };
                    this.reserves.push(newItem);
                    this.$notify('success', 'Резерв успешно добавлен');
                } else {
                    response = await this.axios.put(`/reserves/${this.editedItem.id}`, payload);
                    const updatedItem = {
                        ...response.data,
                        cargoType: this.cargoTypes.find((c) => c.id === this.editedItem.cargoTypeId)
                    };
                    Object.assign(this.reserves[this.editedIndex], updatedItem);
                    this.$notify('success', 'Резерв успешно обновлен');
                }
                this.closeDialog();
            } catch (error) {
                console.error('Ошибка сохранения:', error);
                this.$notify('error', error.response?.data || 'Ошибка при сохранении резерва');
            } finally {
                this.saveLoading = false;
            }
        },

        // ========== УДАЛЕНИЕ ==========

        deleteReserve(item) {
            this.deleteItem = item;
            this.deleteDialog = true;
        },

        async confirmDelete() {
            this.deleteLoading = true;
            try {
                await this.axios.delete(`/reserves/${this.deleteItem.id}`);

                const index = this.reserves.indexOf(this.deleteItem);
                if (index !== -1) this.reserves.splice(index, 1);
                this.$notify('success', 'Резерв успешно удален');
                this.deleteDialog = false;
                this.deleteItem = null;
            } catch (error) {
                console.error('Ошибка удаления:', error);
                this.$notify('error', error.response?.data || 'Ошибка при удалении резерва');
            } finally {
                this.deleteLoading = false;
            }
        }
    }
};
</script>

<style scoped>
.dialogTitleBackground {
    background: #1976d2;
    padding: 16px 20px;
}
</style>